// @ts-nocheck
import React, { useEffect, useRef, useCallback, useState } from 'react';

interface TiledVideoBackgroundProps {
    src: string;
    /** Tile width as a percentage of the viewport width (e.g. 33 = 33vw) */
    resolutionVw: number;
    style?: React.CSSProperties;
    className?: string;
}

/** How many seconds before the end to pre-roll the standby video. */
const CROSSFADE_AHEAD = 0.3;

const TiledVideoBackground: React.FC<TiledVideoBackgroundProps> = ({
    src,
    resolutionVw,
    style,
    className,
}) => {
    const canvasRef    = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const rafRef       = useRef<number | null>(null);

    // Double buffer
    const videoA   = useRef<HTMLVideoElement>(null);
    const videoB   = useRef<HTMLVideoElement>(null);
    const activeRef = useRef<'a' | 'b'>('a');

    const resolutionVwRef = useRef(resolutionVw);
    useEffect(() => { resolutionVwRef.current = resolutionVw; }, [resolutionVw]);

    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsVisible(entry.isIntersecting);
            },
            { rootMargin: '200px' } // Pre-load slightly before coming into view
        );
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }
        return () => observer.disconnect();
    }, []);

    const getVideos = useCallback(() => ({
        active:  activeRef.current === 'a' ? videoA.current : videoB.current,
        standby: activeRef.current === 'a' ? videoB.current : videoA.current,
    }), []);

    const draw = useCallback(() => {
        const canvas    = canvasRef.current;
        const container = containerRef.current;
        const { active } = getVideos();

        if (!canvas || !container || !active || active.readyState < 2) {
            rafRef.current = requestAnimationFrame(draw);
            return;
        }

        const cw = container.clientWidth;
        const ch = container.clientHeight;
        if (canvas.width !== cw || canvas.height !== ch) {
            canvas.width  = cw;
            canvas.height = ch;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) { rafRef.current = requestAnimationFrame(draw); return; }

        const vw     = window.innerWidth;
        const tileW  = Math.round((resolutionVwRef.current / 100) * vw);
        const aspect = active.videoWidth && active.videoHeight
            ? active.videoHeight / active.videoWidth
            : 9 / 16;
        const tileH = Math.round(tileW * aspect);

        if (tileW > 0 && tileH > 0) {
            ctx.clearRect(0, 0, cw, ch);
            for (let y = 0; y < ch; y += tileH) {
                for (let x = 0; x < cw; x += tileW) {
                    ctx.drawImage(active, x, y, tileW, tileH);
                }
            }
        }

        const { standby } = getVideos();
        if (
            standby &&
            active.duration > 0 &&
            active.currentTime >= active.duration - CROSSFADE_AHEAD &&
            standby.paused
        ) {
            standby.currentTime = 0;
            standby.play().catch(() => {});
        }

        rafRef.current = requestAnimationFrame(draw);
    }, [getVideos]);

    useEffect(() => {
        const a = videoA.current;
        const b = videoB.current;
        if (!a || !b) return;

        // If not visible, pause both videos and stop RAF to prevent massive CPU drain
        if (!isVisible) {
            if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
            a.pause();
            b.pause();
            return;
        }

        // When visible, resume
        [a, b].forEach(v => {
            if (v.src !== src) {
                v.src = src;
                v.load();
            }
        });

        const active = getVideos().active;
        active.play().catch(() => {});
        if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(draw);

        const onEndedA = () => {
            if (activeRef.current === 'a') {
                activeRef.current = 'b';
                a.currentTime = 0;
                a.pause();
            }
        };
        const onEndedB = () => {
            if (activeRef.current === 'b') {
                activeRef.current = 'a';
                b.currentTime = 0;
                b.pause();
                if (a.paused) { a.currentTime = 0; a.play().catch(() => {}); }
            }
        };

        a.addEventListener('ended', onEndedA);
        b.addEventListener('ended', onEndedB);

        return () => {
            a.removeEventListener('ended', onEndedA);
            b.removeEventListener('ended', onEndedB);
            if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
        };
    }, [src, draw, isVisible, getVideos]);

    const offscreenStyle: React.CSSProperties = {
        position: 'absolute',
        left: '-9999px',
        top: '-9999px',
        width: '1px',
        height: '1px',
        pointerEvents: 'none',
    };

    return (
        <div
            ref={containerRef}
            className={className}
            style={{ position: 'absolute', inset: 0, overflow: 'hidden', ...style }}
        >
            <video ref={videoA} muted playsInline preload="auto" style={offscreenStyle} />
            <video ref={videoB} muted playsInline preload="auto" style={offscreenStyle} />
            <canvas
                ref={canvasRef}
                style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    display: 'block',
                    pointerEvents: 'none',
                }}
            />
        </div>
    );
};

export default TiledVideoBackground;
